import { useId, useState } from 'react';
import { Button, FormField, Textarea } from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatDateTime } from '../../lib/format/dates';
import { notify } from '../../services/notify';
import type { AdminNote } from './api';
import { SectionLabel } from './people';
import { useAddAdminNote } from './queries';

/**
 * Internal admin notes — the Notes tab of the Chef and Eater dialogs.
 * Append-only (`POST /admin/profiles/{id}/notes`), newest first.
 */
export function AdminNotesPanel({ profileId, notes, noun }: { profileId: string; notes: AdminNote[]; noun: string }) {
  const fieldId = useId();
  const { profile: me, locked } = useSession();
  const lock = locked('crm.write');
  const addNote = useAddAdminNote(profileId);
  const [text, setText] = useState('');
  const newestFirst = [...notes].reverse();

  return (
    <div>
      <FormField label="Add a note" htmlFor={fieldId} hint={`Internal only — never shown to the ${noun}.`}>
        <Textarea
          id={fieldId}
          surface="white"
          rows={3}
          placeholder={`Add a note about this ${noun}…`}
          value={text}
          disabled={!!lock}
          onChange={(e) => setText(e.target.value)}
        />
      </FormField>
      <div className="mt-2.5 flex justify-end">
        <Button
          size="sm"
          disabled={!text.trim()}
          loading={addNote.isPending}
          lockedReason={lock}
          onClick={() =>
            addNote.mutate(text.trim(), {
              onSuccess: () => {
                setText('');
                notify.success('Note added.');
              },
            })
          }
        >
          Add note
        </Button>
      </div>

      <div className="mt-[18px]">
        <SectionLabel>Notes</SectionLabel>
        {newestFirst.length === 0 ? (
          <div className="text-[13px] text-muted">No notes yet.</div>
        ) : (
          newestFirst.map((n) => (
            <div key={n.id} className="border-t border-[rgba(143,68,32,.08)] py-2.5 text-sm">
              <div className="mb-1 text-xs text-muted">
                {formatDateTime(n.at)}
                {n.byProfileId && ` · ${n.byProfileId === me?.id ? 'you' : n.byProfileId}`}
              </div>
              <div className="whitespace-pre-wrap">{n.text}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
